<script setup lang="ts">
/**
 * Client feedback: the keys a client site's feedback button sends with
 * (server/feedback.ts), and for each the three ways onto a site — a script tag,
 * a bookmarklet and the WordPress plugin. The corner is only the snippet's: the
 * key does not care where the button sits.
 *
 * With `repo` it is that repo's, in its settings card (RepoView.vue): its keys,
 * and the form for a new one, the repo set by where it is — a key is made where
 * its repo is. Without, it is Global settings': every key (older ones may name
 * no repo), and what the board's own button files under, but no form.
 *
 * Feedback is a root task unless a task is picked for it to go under: one per
 * key (in the key's team), and one for the board's own button.
 *
 * "Ask for email" is chosen when a key is made: it is baked into the snippets
 * (`data-email`) and the plugin, which a site already carries, so it is not changed after.
 *
 * Hidden fields, like the corner, are only the snippets': `name=value` pairs baked
 * into the script tag and the bookmarklet as `data-field-*`. A value that changes
 * (a build, who is signed in) the page sets itself with `TodosFeedback.setFields()`,
 * and a WordPress site with the plugin's `todos_feedback_fields` filter.
 */
import { computed, onMounted, reactive, ref, watch } from "vue";
import { api } from "../lib/api.js";
import { copyText } from "../lib/clipboard.js";
import { toast } from "../lib/toast.js";
import { boardParentId, feedbackKeys as allKeys, feedbackKeysError, loadFeedbackKeys } from "../lib/feedbackKeys.js";
import { useBoard } from "../stores/board.js";
import TaskPicker from "./TaskPicker.vue";
import { FEEDBACK_CORNERS, FEEDBACK_LABEL_MAX, type FeedbackCorner, type FeedbackKey } from "@shared/feedback.js";

/** `teams`: the ids of the teams whose boards list the repo (`RepoInfo.teams`). */
const props = defineProps<{ repo?: string; teams?: string[] }>();

const board = useBoard();
const actionError = ref("");
const error = computed(() => actionError.value || feedbackKeysError.value);
const busy = ref(false);

const keys = computed(() => (props.repo ? allKeys.value?.filter((k) => k.repo === props.repo) : allKeys.value) ?? null);

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

// ── what feedback goes under ──────────────────────────────────────────────
async function setBoardParent(parentId: string | null): Promise<void> {
  actionError.value = "";
  try {
    boardParentId.value = (await api.put<{ boardParentId: string | null }>("/api/feedback-keys/board-parent", { parentId })).boardParentId;
    toast(parentId ? "The board's feedback goes under that task" : "The board's feedback is a task of its own again");
  } catch (err) {
    actionError.value = message(err);
  }
}

async function setParent(k: FeedbackKey, parentId: string | null): Promise<void> {
  actionError.value = "";
  try {
    const { key } = await api.patch<{ key: FeedbackKey }>(`/api/feedback-keys/${k.key}`, { parentId });
    allKeys.value = (allKeys.value ?? []).map((x) => (x.key === key.key ? key : x));
    toast(parentId ? `${k.label}'s feedback goes under that task` : `${k.label}'s feedback is a task of its own again`);
  } catch (err) {
    actionError.value = message(err);
  }
}

// ── a new key, in the repo's settings ─────────────────────────────────────
/** The teams a key for this repo can send to: those the reader is in whose
 *  boards list the repo, else (an older server, or none listing it) any of theirs. */
const teamChoices = computed(() => {
  const listing = board.teams.filter((t) => props.teams?.includes(t.id));
  return listing.length ? listing : board.teams;
});
const label = ref("");
const teamId = ref("");
watch(
  teamChoices,
  (choices) => {
    if (choices.some((t) => t.id === teamId.value)) return;
    teamId.value = (choices.find((t) => t.id === board.teamId) ?? choices[0])?.id ?? "";
  },
  { immediate: true },
);
const parentId = ref<string | null>(null);
const askEmail = ref(false);
// A sub-task is in its parent's team, so another team's pick cannot stand.
watch(teamId, () => (parentId.value = null));

async function create(): Promise<void> {
  if (!props.repo || !label.value.trim() || !teamId.value || busy.value) return;
  busy.value = true;
  actionError.value = "";
  try {
    const { key } = await api.post<{ key: FeedbackKey }>("/api/feedback-keys", {
      label: label.value,
      teamId: teamId.value,
      repo: props.repo,
      parentId: parentId.value,
      askEmail: askEmail.value,
    });
    allKeys.value = [...(allKeys.value ?? []), key];
    label.value = "";
    parentId.value = null;
    askEmail.value = false;
  } catch (err) {
    actionError.value = message(err);
  } finally {
    busy.value = false;
  }
}

/** Two clicks: sites carrying it stop working, and nothing brings the key back. */
const confirming = ref<string | null>(null);
async function remove(k: FeedbackKey): Promise<void> {
  if (confirming.value !== k.key) {
    confirming.value = k.key;
    return;
  }
  confirming.value = null;
  try {
    await api.del(`/api/feedback-keys/${k.key}`);
    allKeys.value = (allKeys.value ?? []).filter((x) => x.key !== k.key);
    toast(`Deleted the key for ${k.label}`);
  } catch (err) {
    actionError.value = message(err);
  }
}

// ── the snippets ──────────────────────────────────────────────────────────
const origin = location.origin;
const corners = reactive<Record<string, FeedbackCorner>>({});
const cornerOf = (k: FeedbackKey): FeedbackCorner => corners[k.key] ?? "bottom-right";

/** Per key, as typed: "build=1.4.2, env=staging". */
const fieldText = reactive<Record<string, string>>({});

/** The typed pairs a snippet can carry: a name is what an HTML attribute may be
 *  after `data-field-` (lower-cased, as the browser would), a blank value is dropped. */
function fieldsOf(k: FeedbackKey): [string, string][] {
  const out: [string, string][] = [];
  for (const part of (fieldText[k.key] ?? "").split(",")) {
    const at = part.indexOf("=");
    if (at < 0) continue;
    const name = part.slice(0, at).trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
    const value = part.slice(at + 1).trim();
    if (name && value) out.push([name, value]);
  }
  return out;
}
const attr = (v: string): string => v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
/** Inside the bookmarklet's single-quoted strings, in a javascript: URL, which is percent-decoded first. */
const jsStr = (v: string): string => v.replace(/[\\']/g, "\\$&").replace(/%/g, "%25");

const scriptTag = (k: FeedbackKey): string =>
  `<script src="${origin}/feedback.js" data-key="${k.key}" data-corner="${cornerOf(k)}"${k.askEmail ? ' data-email="1"' : ""}` +
  fieldsOf(k).map(([n, v]) => ` data-field-${n}="${attr(v)}"`).join("") +
  ` async><\/script>`;

/** For a value the page knows only as it runs; the names typed, else an example. */
const setFieldsCall = (k: FeedbackKey): string => {
  const names = fieldsOf(k).map(([n]) => n);
  return `TodosFeedback.setFields({ ${(names.length ? names : ["build"]).map((n) => `${/^[a-z_][a-z0-9_]*$/.test(n) ? n : JSON.stringify(n)}: "…"`).join(", ")} });`;
};

/** Loads the same script into whatever page it is clicked on, opened. Clicked
 *  again on a page that has it, it just opens it. */
const bookmarklet = (k: FeedbackKey): string =>
  "javascript:(function(){if(window.TodosFeedback){window.TodosFeedback.open();return}" +
  `var s=document.createElement('script');s.src='${origin}/feedback.js';` +
  `s.setAttribute('data-key','${k.key}');s.setAttribute('data-corner','${cornerOf(k)}');s.setAttribute('data-open','1');` +
  (k.askEmail ? "s.setAttribute('data-email','1');" : "") +
  fieldsOf(k).map(([n, v]) => `s.setAttribute('data-field-${n}','${jsStr(v)}');`).join("") +
  "document.body.appendChild(s)})()";

async function copy(text: string, what: string): Promise<void> {
  await copyText(text);
  toast(`Copied the ${what}`);
}

const teamName = (id: string): string => board.teams.find((t) => t.id === id)?.name ?? "a team you are not in";
const when = (ms: number | null): string => (ms === null ? "never" : new Date(ms).toLocaleString());
const canCreate = computed(() => !!label.value.trim() && !!teamId.value && !busy.value);

onMounted(() => void loadFeedbackKeys());
</script>

<template>
  <div class="feedback-keys" :class="repo ? 'repo-feedback' : 'money-card profile-card'">
    <h3>Client feedback</h3>
    <p v-if="repo" class="money-hint">
      A floating button for a client's site: what they type is sent with the page's address and lands as a
      task in {{ repo }}, in the key's team, as you — a task of its own, or a sub-task of the one picked for
      it — and is queued for Claude. The key sits in the page for anyone to read, so all it can do is add
      tasks; delete it to stop a site sending.
    </p>
    <p v-else class="money-hint">
      Every client site's feedback key. A key is made in its repo's settings (the Repos view, or the git
      panel's 🛠), which sets the repo; here they can be moved under a task, embedded or deleted.
    </p>

    <div v-if="!repo" class="integration-row feedback-under">
      <span>This board's own button files under</span>
      <TaskPicker
        :model-value="boardParentId ?? null"
        :disabled="boardParentId === undefined"
        placeholder="no task: a root on the board on screen"
        @update:model-value="setBoardParent"
      />
    </div>

    <template v-if="repo">
      <form class="integration-row feedback-new" @submit.prevent="create">
        <input
          v-model="label"
          :maxlength="FEEDBACK_LABEL_MAX"
          placeholder="Topic — e.g. Acme shop"
          aria-label="Topic"
          autocomplete="off"
          :disabled="busy"
        />
        <select v-if="teamChoices.length > 1" v-model="teamId" aria-label="Team" :disabled="busy">
          <option v-for="t in teamChoices" :key="t.id" :value="t.id">{{ t.name }}</option>
        </select>
        <TaskPicker v-model="parentId" :team-id="teamId" :disabled="busy" placeholder="under task (optional)" />
        <label class="feedback-ask-email" title="The button gets a box for the sender's email, which they may leave blank">
          <input v-model="askEmail" type="checkbox" :disabled="busy" /> Ask for email
        </label>
        <button class="btn primary" type="submit" :disabled="!canCreate">Add key</button>
      </form>
      <p v-if="!board.teams.length" class="integration-muted">You are in no team: a key needs one to send to.</p>
    </template>
    <p v-if="error" class="integration-error">{{ error }}</p>

    <p v-if="keys && !keys.length" class="integration-muted">No keys yet.</p>
    <div v-for="k in keys ?? []" :key="k.key" class="feedback-key">
      <div class="integration-row feedback-key-head">
        <strong>{{ k.label }}</strong>
        <span class="integration-muted">
          → {{ teamName(k.teamId) }}<template v-if="k.repo && !repo"> · {{ k.repo }}</template>
          · {{ k.uses }} sent · last {{ when(k.lastUsedAt) }}<template v-if="k.askEmail"> · asks for email</template>
        </span>
        <span style="flex: 1"></span>
        <select
          :value="cornerOf(k)"
          aria-label="Corner"
          title="Which corner the button sits in"
          @change="corners[k.key] = ($event.target as HTMLSelectElement).value as FeedbackCorner"
        >
          <option v-for="c in FEEDBACK_CORNERS" :key="c" :value="c">{{ c }}</option>
        </select>
        <button
          class="btn ghost"
          :class="{ danger: confirming === k.key }"
          type="button"
          @click="remove(k)"
          @blur="confirming = null"
        >{{ confirming === k.key ? "Really delete?" : "Delete" }}</button>
      </div>

      <div class="integration-row">
        <span class="feedback-way">Under</span>
        <TaskPicker
          :model-value="k.parentId"
          :team-id="k.teamId"
          placeholder="no task: each is a task of its own"
          @update:model-value="setParent(k, $event)"
        />
      </div>
      <div class="integration-row">
        <span class="feedback-way">Fields</span>
        <input
          v-model="fieldText[k.key]"
          class="feedback-fields"
          type="text"
          placeholder="build=1.4.2, env=staging"
          aria-label="Hidden fields"
          title="Sent with every piece of feedback, never shown: name=value, comma-separated"
        />
        <span class="integration-muted">hidden, sent with each; baked into the tag and bookmarklet below</span>
      </div>
      <div class="integration-row">
        <span class="feedback-way">Embed</span>
        <code class="integration-code">{{ scriptTag(k) }}</code>
        <button class="btn" type="button" @click="copy(scriptTag(k), 'script tag')">Copy</button>
      </div>
      <div class="integration-row">
        <span class="feedback-way" title="For values only the page knows as it runs">Dynamic</span>
        <code class="integration-code">{{ setFieldsCall(k) }}</code>
        <button class="btn" type="button" @click="copy(setFieldsCall(k), 'setFields call')">Copy</button>
      </div>
      <div class="integration-row">
        <span class="feedback-way">Bookmarklet</span>
        <a
          class="btn"
          :href="bookmarklet(k)"
          title="Drag this to the bookmarks bar, then click it on any page of the site"
          @click.prevent
        >💬 {{ k.label }} feedback</a>
        <span class="integration-muted">drag to the bookmarks bar</span>
        <span style="flex: 1"></span>
        <button class="btn" type="button" @click="copy(bookmarklet(k), 'bookmarklet')">Copy</button>
      </div>
      <div class="integration-row">
        <span class="feedback-way">WordPress</span>
        <a class="btn" :href="`/api/feedback-keys/${k.key}/wordpress.zip`" download="todos-feedback.zip">Download plugin</a>
        <span class="integration-muted">Plugins → Add New → Upload; the corner and who sees it (everyone, people logged in, or administrators only) are under Settings → Todos feedback; hidden fields come from the <code>todos_feedback_fields</code> filter</span>
      </div>
    </div>
  </div>
</template>
